import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Snackbar } from '@mui/material';
import { userEventService } from '../services/userEventService';
import { useAuth } from './AuthContext';
import { SignupLoginPrompt } from '../components/common/SignupLoginPrompt';
import logger from '../utils/logger';

export interface UserEventContextValue {
  myEventIds: Set<string>;
  loading: boolean;
  error: string | null;
  add: (eventId: string) => Promise<void>;
  remove: (eventId: string) => Promise<void>;
  refresh: () => Promise<void>;
  showSnackbar: (message: string) => void;
  showSignupPrompt: () => void;
}

const UserEventContext = createContext<UserEventContextValue | undefined>(undefined);

export const UserEventProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [myEventIds, setMyEventIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [signupPromptOpen, setSignupPromptOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setMyEventIds(new Set());
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await userEventService.listMyEvents();
      setMyEventIds(new Set(res.items.map((item) => item.event_id)));
    } catch (e) {
      logger.error('Failed to load my events:', e);
      setError('MyEvent 一覧の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Refresh when auth state settles
  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, user, refresh]);

  const add = useCallback(async (eventId: string) => {
    // Optimistic update
    setMyEventIds((prev) => {
      const next = new Set(prev);
      next.add(eventId);
      return next;
    });
    try {
      await userEventService.addMyEvent(eventId);
    } catch (e) {
      // Rollback
      setMyEventIds((prev) => {
        const next = new Set(prev);
        next.delete(eventId);
        return next;
      });
      throw e;
    }
  }, []);

  const remove = useCallback(async (eventId: string) => {
    // Optimistic update
    setMyEventIds((prev) => {
      const next = new Set(prev);
      next.delete(eventId);
      return next;
    });
    try {
      await userEventService.removeMyEvent(eventId);
    } catch (e) {
      // Rollback
      setMyEventIds((prev) => {
        const next = new Set(prev);
        next.add(eventId);
        return next;
      });
      throw e;
    }
  }, []);

  const showSnackbar = useCallback((message: string) => setSnackbarMessage(message), []);
  const showSignupPrompt = useCallback(() => setSignupPromptOpen(true), []);

  return (
    <UserEventContext.Provider value={{
      myEventIds, loading, error, add, remove, refresh, showSnackbar, showSignupPrompt,
    }}>
      {children}
      <SignupLoginPrompt open={signupPromptOpen} onClose={() => setSignupPromptOpen(false)} />
      <Snackbar
        open={snackbarMessage !== null}
        message={snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </UserEventContext.Provider>
  );
};

export const useUserEvent = (): UserEventContextValue => {
  const ctx = useContext(UserEventContext);
  if (!ctx) throw new Error('useUserEvent must be used within UserEventProvider');
  return ctx;
};
